import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useProductLine } from "@/platform/ProductLineProvider";
import {
  PRODUCT_LINES,
  PRODUCT_LINE_HOME,
  productLineLabel,
  type ProductLine,
} from "@/platform/productLine";

import { buttonClass } from "@/components/buttonStyles";
// The top-bar product-line switcher — the pill next to the Brand that rotates
// the whole portal between the Commercial and Surety platforms. Picking a line
// just navigates to that platform's home; the active line (and so the left nav
// and this trigger's label) is derived from the route, so the switcher has
// nothing else to set (BRK-S090). Same open/close affordances as the account
// menu (aria-expanded, closes on Escape / outside click / selection).
//
// Below sm the header has no room for it, so PortalShell hides the header's
// copy and the open drawer carries one at its top instead, filling the drawer's
// width (BRK-S248).
export function ProductLineSwitcher({
  className = "",
  fill = false,
}: {
  /** Classes for the wrapper: where the pill shows, e.g. `hidden sm:block`. */
  className?: string;
  /** Full width, with the chevron at the far edge (the drawer's copy). */
  fill?: boolean;
} = {}) {
  const { productLine } = useProductLine();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function onPointerDown(event: MouseEvent) {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setOpen(false);
      }
    }
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  function choose(line: ProductLine) {
    setOpen(false);
    if (line !== productLine) navigate(PRODUCT_LINE_HOME[line]);
  }

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      <button
        type="button"
        aria-haspopup="true"
        aria-expanded={open}
        aria-label={`Platform: ${productLineLabel(productLine)}`}
        onClick={() => setOpen((prev) => !prev)}
        className={`${buttonClass({ variant: "secondary", size: "sm" })} ${
          fill ? "w-full" : ""
        }`}
      >
        <span
          aria-hidden="true"
          className="inline-block h-2 w-2 rounded-full bg-brk-accent"
        />
        <span className={fill ? "flex-1 text-left" : undefined}>
          {productLineLabel(productLine)}
        </span>
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          aria-hidden="true"
          className={`shrink-0 text-brk-dim transition-transform ${open ? "rotate-180" : ""}`}
        >
          <path
            d="m6 9 6 6 6-6"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      {open && (
        <div
          role="menu"
          className={`absolute left-0 z-40 mt-1 overflow-hidden rounded-md border border-brk-border bg-brk-panel py-1 shadow-lg ${
            fill ? "w-full" : "w-52"
          }`}
        >
          {PRODUCT_LINES.map((line) => {
            const active = line.id === productLine;
            return (
              <button
                key={line.id}
                type="button"
                role="menuitemradio"
                aria-checked={active}
                onClick={() => choose(line.id)}
                className={`flex w-full items-center justify-between gap-2 px-4 py-2 text-left text-sm transition-colors hover:bg-brk-bg ${
                  active ? "font-semibold text-brk-accent" : "text-brk-text"
                }`}
              >
                <span className="flex items-center gap-2">
                  <span
                    aria-hidden="true"
                    className={`inline-block h-2 w-2 rounded-full ${active ? "bg-brk-accent" : "bg-brk-border"}`}
                  />
                  {line.label}
                </span>
                {active && (
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    aria-hidden="true"
                    className="text-brk-accent"
                  >
                    <path
                      d="m5 12 5 5 9-11"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
